import requestInstance from './Request';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_ENDPOINT } from './Api';

class RateService {
  constructor() {
    // Get base URL from environment variable
    this.baseUrl = process.env.REACT_APP_B2B_API_BASE_URL || '';
    this.rateCardUrl = `${this.baseUrl}${API_ENDPOINT.RateCard.apiv2}`;
  }

  getTripTypeId(rideType) {
    const tripTypes = {
      'Ride': 1,
      'Airport Transfer': 4,
      'Rental': 5,
      'Outstation': 7
    };
     const tripTypeId = tripTypes[rideType];
    return tripTypes[rideType] || rideType;
  }

  // Add day mapping helper
  getDayFullName(shortDay) {
    const dayMap = {
      'Mon': 'Monday',
      'Tue': 'Tuesday',
      'Wed': 'Wednesday',
      'Thu': 'Thursday',
      'Fri': 'Friday',
      'Sat': 'Saturday',
      'Sun': 'Sunday'
    };
    return dayMap[shortDay] || shortDay;
  }

transformPackagesForApi(packages) {
  return (packages || []).map(pkg => {
    const additionalCharges = [];

    // Handle extra KM
    const extraKm = pkg.additionalCharges?.extraKm;
    if (extraKm?.enabled) {
      additionalCharges.push({
        name: 'extraKm',
        label: 'Extra KM',
        unitType: 'Rs/km',
        rate: parseFloat(extraKm.extraKmCharge || 0),
        graceValue: parseFloat(extraKm.graceKm || 0)
      });
    }

    // Handle extra Time
    const extraTime = pkg.additionalCharges?.extraTime;
    if (extraTime?.enabled) {
      additionalCharges.push({
        name: 'extraTime',
        label: 'Extra Time',
        unitType: 'Rs/min',
        rate: parseFloat(extraTime.extraTimeCharge || 0),
        graceValue: parseFloat(extraTime.graceTime || 0)
      });
    }

    return {
      name: pkg.name,
      duration: parseFloat(pkg.duration || 0),
      durationType: pkg.durationType.toLowerCase(), // e.g., "Hour" -> "hour"
      maxDistanceKms: parseFloat(pkg.maxDistanceKms || 0),
      cost: parseFloat(pkg.cost || 0),
      additionalCharges
    };
  });
}


  async createRateCard(formData, locationId, contractId, dutyTypeOptions = []) {
    try {
      // Use locationId from parameter first, fallback to formData.locationId
      const finalLocationId = locationId || formData.locationId;

      if (!finalLocationId) {
        throw new Error('Location ID is required');
      }

      const payload = {
  location: finalLocationId,
  tripType: dutyTypeOptions.find(option => option.value === formData.rideType)?.tripType || this.getTripTypeId(formData.rideType),
  carType: formData.carType,
  contractId,
  baseRate: parseFloat(formData.baseRate || 0),
  surCharge: parseFloat(formData.surCharge || 0),
  platformCharge: parseFloat(formData.platformCharge || 0),
  isMultiDay: formData.isMultiDay || false,
};

// Set rateType and rateConfig based on selected type
if (formData.rateType === 'package') {
  payload.rateType = 'package';
  payload.rateConfig = this.transformPackagesForApi(formData.packages)  // <- FIXED
}
 else {
  payload.rateType = 'flat_per_km';
  payload.rateConfig = {
    base_rate_per_km: parseFloat(formData.kmCharge || 0)
  };
}

      const response = await requestInstance.post(this.rateCardUrl, payload);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Service Error:', error);
      toast.error(error.response?.data?.message)
      return {
        success: false,
        error: error.message || error.response?.data?.message || 'Failed to create rate card'
      };
    }
  }

  // Update getRateCard method to better handle existing configurations
  async getRateCard(locationId, rideType, carType, contractId, dutyTypeOptions = [], rateType, isMultiDay) {
    try {
      const tripType = rideType ? this.getTripTypeId(rideType) : null;
      
      let url = `${this.rateCardUrl}?location=${locationId}&tripType=${tripType}&carType=${carType}&rateType=${rateType}&contractId=${contractId}&isMultiDay=${isMultiDay}`;
        
      const response = await requestInstance.get(url);

      // Helper function to get ride type name from ID
      const getRideTypeFromId = (id) => {
        const tripTypes = dutyTypeOptions.reduce((acc, option) => {
          acc[option.value] = option.tripType;
          return acc;
        }, {});
        return Object.keys(tripTypes).find(key => tripTypes[key] === id) || '';
      };

      if (!response.data.success || !response.data.data) {
        return {
          success: true,
          data: {
            formattedData: {
              locationId: { id: locationId }, // Format location as object
              rideType: rideType || '',
              carType: { id: carType }, // Format car type as object
              contractId,
              rateType: '',
              baseRate: '',
              surCharge: '',
              platformCharge: '',
              waitingTime: '',
              bufferTime: '',
              extraKmCharge: '',
              graceKm: '',
              extraTimeCharge: '',
              graceTime: '',
               kmCharge: '', // Add this to reset kmCharge
               rateConfig: {  // Add this to reset rate config
                 base_rate_per_km: ''
               }
            },
            hasExistingConfig: false,
            filters: {
              location: { id: locationId },
              rideType,
              carType: { id: carType },
              contractId
            }
          }
        };
      }

      const matchingConfig = response.data.data.find(card => {
        const locationMatch = card.location === locationId;
        const tripTypeMatch = !tripType || card.tripType === tripType;
        const carTypeMatch = !carType || card.carType === carType;
        
        return locationMatch && tripTypeMatch && carTypeMatch && contractId;
      });

      if (matchingConfig) {
        const waitingTimeCharge = matchingConfig.RateCardExtraCharges?.find(c => c.name === 'waitingTime') || {};
        const extraKmCharge = matchingConfig.RateCardExtraCharges?.find(c => c.name === 'extraKm') || {};
        const extraTimeCharge = matchingConfig.RateCardExtraCharges?.find(c => c.name === 'extraTime') || {};

        const matchedRideType = getRideTypeFromId(matchingConfig.tripType);

        // Format the data with proper object structure for location and carType
        const formattedData = {
          id: matchingConfig.id,
          locationId: { 
            id: matchingConfig.location,
            name: matchingConfig.locationName || ''
          },
          rideType: rideType || matchedRideType,
          carType: { 
            id: matchingConfig.carType,
            name: matchingConfig.carTypeName || ''
          },
          contractId,
          rateType: matchingConfig.rateType === 'flat_per_km' ? 'km' : matchingConfig.rateType,
          baseRate: matchingConfig.baseRate?.toString() || '0',
          surCharge: matchingConfig.surCharge?.toString() || '0',
          platformCharge: matchingConfig.platformCharge?.toString() || '0',
          kmCharge: matchingConfig.rateConfig?.base_rate_per_km?.toString() || '0',
          waitingTime: waitingTimeCharge.rate?.toString() || '0',
          bufferTime: waitingTimeCharge.graceValue?.toString() || '0',
          extraKmCharge: extraKmCharge.rate?.toString() || '0',
          graceKm: extraKmCharge.graceValue?.toString() || '0',
          extraTimeCharge: extraTimeCharge.rate?.toString() || '0',
          graceTime: extraTimeCharge.graceValue?.toString() || '0'
        };

        return {
          success: true,
          data: {
            ...response.data,
            data: [matchingConfig],
            formattedData,
            hasExistingConfig: true,
            filters: {
              location: { 
                id: matchingConfig.location,
                name: matchingConfig.locationName || ''
              },
              rideType: matchedRideType,
              carType: { 
                id: matchingConfig.carType,
                name: matchingConfig.carTypeName || ''
              }
            }
          }
        };
      }

      // No matching config found - return empty form with preserved selections
      return {
        success: true,
        data: {
          ...response.data,
          formattedData: {
            locationId: { id: locationId }, // Format location as object
            rideType: rideType || '',
            carType: { id: carType }, // Format car type as object
            contractId,
            rateType: '',
            baseRate: '',
            surCharge: '',
            platformCharge: '',
            waitingTime: '',
            bufferTime: '',
            extraKmCharge: '',
            graceKm: '',
            extraTimeCharge: '',
            graceTime: '',
            kmCharge: '', // Add this to reset kmCharge
            rateConfig: {  // Add this to reset rate config
              base_rate_per_km: ''
            }
          },
          hasExistingConfig: false,
          filters: {
            location: { id: locationId },
            rideType,
            carType: { id: carType },
            contractId
          }
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch rate card',
        hasExistingConfig: false,
        data: {
          formattedData: {
            locationId: { id: locationId },
            rideType: rideType || '',
            carType: { id: carType },
            contractId
          },
          filters: {
            location: { id: locationId },
            rideType,
            carType: { id: carType },
            contractId
          }
        }
      };
    }
  }

async updateRateCard(id, formData) {
  try {
    const payload = {
      baseRate: parseFloat(formData.baseRate || 0),
      surCharge: parseFloat(formData.surCharge || 0),
      platformCharge: parseFloat(formData.platformCharge || 0)
    };

    if (formData.rateType === 'package') {
      payload.rateConfig = this.transformPackagesForApi(formData.packages);
    } else {
      payload.rateConfig = {
        base_rate_per_km: parseFloat(formData.kmCharge || 0)
      };
    }

    const response = await requestInstance.put(`${this.rateCardUrl}/${id}`, payload);

    if (response.data.success) {
      toast.success('Rate card updated successfully');
      this.getRateCard(response.data.data.location, response.data.data.tripType, response.data.data.carType);
    }

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Service Error:', error);
    toast.error(error.response?.data?.message || 'Failed to update rate card');
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to update rate card'
    };
  }
}

async getPackageNameDropdown(contractId) {
  try {
    const url = `${this.baseUrl}/company/v2/rate-card-types?rateCardType=package&contractId=${contractId}`;
    const response = await requestInstance.get(url);
    return response.data?.data || [];
  } catch (error) {
    console.error("Error fetching package names:", error);
    return [];
  }
}

async getPackages({ locationId, tripType, carType, contractId, isMultiDay = false }) {
  try {
    const params = new URLSearchParams({
      location: locationId,
      tripType: this.getTripTypeId(tripType),
      carType,
      rateType: 'package',
      contractId,
      isMultiDay: isMultiDay
    });

    const response = await requestInstance.get(`${this.rateCardUrl}?${params}`);
    return response;
  } catch (error) {
    console.error('Error fetching package config:', error);
    return null;
  }
}


async savePackageRateCard(packageData, metaData) {
  try {
    const payload = {
      location: metaData.locationId,
      tripType: this.getTripTypeId(metaData.rideType),
      carType: metaData.carType,
      contractId: metaData.contractId,
      baseRate: parseFloat(packageData.baseRate),
      surCharge: parseFloat(packageData.surCharge),
      platformCharge: parseFloat(packageData.platformCharge),
      isMultiDay: metaData.isMultiDay || false,
      rateType: 'package',
      rateConfig: {
        rateCardTypeId: packageData.rateCardTypeId || '',
        duration: parseFloat(packageData.duration),
        durationType: packageData.durationType,
        maxDistanceKms: parseFloat(packageData.maxDistanceKms || 0),
        cost: parseFloat(packageData.cost),
        extraKM: {
          rate: parseFloat(packageData.extraKM?.rate || 0),
          unitType: 'KM',
          graceValue: parseFloat(packageData.extraKM?.graceValue || 0)
        },
        extraHrs: {
          rate: parseFloat(packageData.extraHrs?.rate || 0),
          unitType: packageData.extraHrs?.unitType || 'Hour',
          graceValue: parseFloat(packageData.extraHrs?.graceValue || 0)
        },
        waitingTime: {
          rate: parseFloat(packageData.waitingTime?.rate || 0),
          unitType: 'Min',
          graceValue: parseFloat(packageData.waitingTime?.graceValue || 0),
        },
        nightAllowance: {
          rate: parseFloat(packageData.nightAllowance?.rate || 0),
          unitType: 'Rs',
          graceValue: parseFloat(packageData.nightAllowance?.graceValue || 0),
        },
        driverAllowance: {
          rate: parseFloat(packageData.driverAllowance?.rate || 0),
          unitType: 'Rs',
          graceValue: parseFloat(packageData.driverAllowance?.graceValue || 0),
        },
        additionalCharges: packageData.additionalCharges || []
      }
    };

    const response = await requestInstance.post(this.rateCardUrl, payload, {
      headers: {
        "Content-Type": "application/json"
      }
    });
    toast.success('Package saved successfully');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error saving package:', error);
    toast.error(error.response?.data?.message || 'Failed to save package');
    return { success: false, error };
  }
}

async updatePackageRateCard(rateCardId, packageData, metaData) {
  try {
    const payload = {
      baseRate: parseFloat(packageData.baseRate),
      surCharge: parseFloat(packageData.surCharge),
      platformCharge: parseFloat(packageData.platformCharge),
      isMultiDay: metaData.isMultiDay || false,
      rateConfig: {
        duration: parseFloat(packageData.duration),
        durationType: packageData.durationType,
        maxDistanceKms: parseFloat(packageData.maxDistanceKms || 0),
        cost: parseFloat(packageData.cost),
        extraKM: {
          rate: parseFloat(packageData.extraKM?.rate || 0),
          unitType: 'KM',
          graceValue: parseFloat(packageData.extraKM?.graceValue || 0)
        },
        extraHrs: {
          rate: parseFloat(packageData.extraHrs?.rate || 0),
          unitType: packageData.extraHrs?.unitType || 'Hour',
          graceValue: parseFloat(packageData.extraHrs?.graceValue || 0)
        },
        waitingTime: {
          rate: parseFloat(packageData.waitingTime?.rate || 0),
          unitType: 'Min',
          graceValue: parseFloat(packageData.waitingTime?.graceValue || 0),
        },
        nightAllowance: {
          rate: parseFloat(packageData.nightAllowance?.rate || 0),
          unitType: 'Rs',
          graceValue: parseFloat(packageData.nightAllowance?.graceValue || 0),
        },
        driverAllowance: {
          rate: parseFloat(packageData.driverAllowance?.rate || 0),
          unitType: 'Rs',
          graceValue: parseFloat(packageData.driverAllowance?.graceValue || 0),
        },
        additionalCharges: packageData.additionalCharges || []
      }
    };

    const response = await requestInstance.put(`${this.rateCardUrl}/${rateCardId}`, payload);
    toast.success('Package updated successfully');
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error updating package:', error);
    toast.error(error.response?.data?.message || 'Failed to update package');
    return { success: false, error };
  }
}

async deleteRateCard(rateCardId) {
  try {
    const response = await requestInstance.delete(`${this.rateCardUrl}/${rateCardId}`);
    if (response.data.success) {
      toast.success('Package deleted successfully');
    }
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error deleting package:', error);
    toast.error(error.response?.data?.message || 'Failed to delete package');
    return { success: false, error };
  }
}

async createNewPackageType(name, description, contractId) {
  try {
    const payload = {
      rateCardType: 'package',
      name,
      description,
      contractId
    };
    const response = await requestInstance.post(`${this.baseUrl}/company/v2/rate-card-types`, payload);
    if (response.data.success) {
      toast.success('Package type created successfully');
      return { success: true, data: response.data.data };
    }
    return { success: false, error: 'Creation failed' };
  } catch (error) {
    console.error('Error creating new package type:', error);
    toast.error(error.response?.data?.message || 'Failed to create new package');
    return { success: false, error };
  }
}

  async getPeakHourConfig(params) {
    try {
      const { location, tripType, carType } = params;
      
      // Convert tripType using getTripTypeId if it exists
      const convertedTripType = tripType ? this.getTripTypeId(tripType) : undefined;
      
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 100,
        location,
        trip_type: convertedTripType, // Use converted trip type
        car_type: carType,
      });

      // Remove undefined parameters
      Array.from(queryParams.keys()).forEach(key => {
        if (queryParams.get(key) === 'undefined') {
          queryParams.delete(key);
        }
      });

      const response = await requestInstance.get(`${this.peakHourUrl}?${queryParams}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching peak hours:', error);
      return {
        success: false,
        error: error.response?.data?.message
      };
    }
  }

  async createPeakHourConfig(data) {
    try {
      const payload = {
        carType: data.carType,
        location: data.locationId,
        contractId: data.contractId,
        tripType: this.getTripTypeId(data.rideType),
        slots: data.slots.map(slot => ({
          day: this.getDayFullName(slot.day), // Convert short day name to full name
          start_time: slot.startTime,
          end_time: slot.endTime,
          adjustment_percent: parseFloat(slot.extraCharge)
        }))
      };

      const response = await requestInstance.post(
        this.peakHourUrl,
        payload
      );
      if(response.data.success) {
        toast.success('Peak hour config created successfully');
      }
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error creating peak hour config:', error);
      toast.error(error.response?.data?.message || 'Failed to create peak hour config');
      return {
        success: false,
        error: error.response?.data?.message
      };
    }
  }

  // Also update updatePeakHourSlot to use full day names
  async updatePeakHourSlot(slotId, data) {
    try {
      const payload = {
        day: this.getDayFullName(data.day),
        start_time: data.startTime,
        end_time: data.endTime,
        adjustment_percent: parseFloat(data.extraCharge)
      };

      const response = await requestInstance.patch(
        `${this.peakHourUrl}/slot/${slotId}`,
        payload
      );
      if(response.data.success) {
        toast.success('Peak hour slot updated successfully');
      }
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error updating peak hour slot:', error);
      toast.error(error.response?.data?.message || 'Failed to update peak hour slot');
      return {
        success: false,
        error: error.response?.data?.message
      };
    }
  }

  async deletePeakHourSlot(slotId) {
    try {
      const payload = {
        is_active: false
      };

      const response = await requestInstance.delete(
        `${this.peakHourUrl}/slot/${slotId}`,
        { data: payload }  // Send payload in the data property for DELETE request
      );
      toast.success('Peak hour slot deleted successfully');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error deleting peak hour slot:', error);
      toast.error(error.response?.data?.message || 'Failed to delete peak hour slot');
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete peak hour slot'
      };
    }
  }

  // Add helper method to get trip types
  getTripTypes() {
    return {
      'Ride': 1,
      'Airport Transfer': 4,
      'Rental': 5,
      'Outstation': 7
    };
  }
}

export default new RateService();